import { logger } from '@fe/core';
import { MessageType, RequestResponseMessageMap } from './actor-messages';

type MessageData = {
    id: string;
    type: MessageType | '<response>' | '<cancel>';
    data?: any;
};

type ResolveReject = {
    resolve: (value?: RequestResponseMessageMap[MessageType][1]) => void;
    reject: (reason?: Error) => void;
};

export type ActorMessage<T extends MessageType> = {
    type: T;
    data?: RequestResponseMessageMap[T][0];
};

export interface ActorTarget {
    addEventListener: typeof window.addEventListener;
    removeEventListener: typeof window.removeEventListener;
    postMessage: typeof window.postMessage;
    terminate?: () => void;
}

export type MessageHandler<T extends MessageType> = (
    params: RequestResponseMessageMap[T][0],
    actor: Actor
) => Promise<RequestResponseMessageMap[T][1]>;

function subscribe(
    target: ActorTarget,
    message: keyof WindowEventMap,
    listener: (...args: any) => void,
    options: boolean | AddEventListenerOptions
): { unsubscribe(): void } {
    target.addEventListener(message, listener, options);
    return {
        unsubscribe: () => {
            target.removeEventListener(message, listener, options);
        }
    };
}

export interface IActor {
    sendAsync<T extends MessageType>(message: ActorMessage<T>): Promise<RequestResponseMessageMap[T][1]>;
}

export class Actor implements IActor {
    private target: ActorTarget;
    private resolveRejects: { [x: string]: ResolveReject };
    private messageHandlers: Partial<Record<MessageType, MessageHandler<MessageType>>>;
    private subscription: { unsubscribe(): void };

    constructor(target: ActorTarget) {
        this.target = target;
        this.resolveRejects = {};
        this.messageHandlers = {};
        this.subscription = subscribe(this.target, 'message', (message) => this.receive(message), false);
    }

    registerMessageHandler<T extends MessageType>(type: T, handler: MessageHandler<T>) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.messageHandlers[type] = handler;
    }

    unregisterMessageHandler(type: MessageType) {
        this.messageHandlers[type] = undefined;
    }

    sendAsync<T extends MessageType>(message: ActorMessage<T>): Promise<RequestResponseMessageMap[T][1]> {
        return new Promise((resolve, reject) => {
            const id = Math.round(Math.random() * 1e18)
                .toString(36)
                .substring(0, 10);
            this.resolveRejects[id] = { resolve, reject };

            // TODO abort controller. Don't really need it right now but there may be tasks that may require cancellation in the future
            const messageToSend: MessageData = {
                id,
                type: message.type,
                data: message.data
            };
            this.target.postMessage(messageToSend); // TODO transferable
        });
    }

    private async receive(message: { data: MessageData }) {
        const data = message.data;
        const id = data.id;

        if (data.type === '<response>') {
            const resolveReject = this.resolveRejects[id];
            delete this.resolveRejects[id];
            if (!resolveReject) {
                logger.debug('No resolve object found for message %s', data.id);
                return;
            }
            resolveReject.resolve(message.data.data);
        } else if (data.type === '<cancel>') {
            logger.debug('Message cancelled');
            const resolveReject = this.resolveRejects[id];
            if (!resolveReject) {
                logger.debug("No reject found to cancel a promise, '%s'", id);
                return;
            }

            if (data.data instanceof Error) {
                resolveReject.reject(data.data);
            } else {
                resolveReject.reject();
            }
            delete this.resolveRejects[id];
        } else {
            const handler = this.messageHandlers[data.type];
            if (!handler) {
                // TODO add error type to the response instead of crashing the entire thing here
                //throw new Error(`Could not find a registered message handler for ${data.type}`);
                logger.debug("Handler not found. Message: '%s'", data.type);
                this.completeTaskError(id, new Error(`Handler not found for message '${data.type}'`));
            } else {
                const params = data.data as RequestResponseMessageMap[MessageType][0];
                try {
                    const result = await handler(params, this);
                    this.completeTask(id, result);
                } catch (error) {
                    logger.error(error);
                    this.completeTaskError(id, error as Error);
                }
            }
        }
    }

    private completeTaskError(id: string, error: Error | null) {
        const response: MessageData = {
            id,
            type: '<cancel>',
            data: error
        };

        this.target.postMessage(response);
    }

    private completeTask(id: string, data: RequestResponseMessageMap[MessageType][1]) {
        const response: MessageData = {
            id,
            type: '<response>',
            data: data
        };
        this.target.postMessage(response); // TODO transferable
    }

    remove() {
        if (this.target instanceof MessagePort) {
            this.target.close();
        }
        this.target.terminate?.();
        this.messageHandlers = {};
        this.subscription.unsubscribe();
    }
}
